import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authenticatedFetch } from '../context/apiClient';
import { useNavigate } from 'react-router-dom';

// Add CSS animation
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;
document.head.appendChild(style);

// Mock calculateImprovement function
const calculateImprovement = (assessment) => {
  const protectedTotal = assessment.total_queries;
  //const unprotectedTotal = assessment.unprotected.blocked + assessment.unprotected.allowed;
  const protected_jailbroken = assessment.total_jailbroken;
  const unprotected_mprovement = ((assessment.original_total_jailbroken / protectedTotal) * 100).toFixed(1);

  return unprotected_mprovement-protected_jailbroken;
};

const HistoryView = () => {
  const [savedAssessments, setSavedAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Load assessments from API on mount
  useEffect(() => {
    const fetchAssessments = async () => {
      if (!user?.token) return;
      setError(null);
      try {
        const response = await authenticatedFetch('/api/rail-history', {}, user.token, logout);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const sortedData = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setSavedAssessments(sortedData);
      } catch (e) {
        console.error("Failed to fetch assessments:", e);
        setError("Failed to load assessments. Please try again later.");
      }
    };
    fetchAssessments();
  }, [user, logout]);

  useEffect(() => {
    const totalPages = Math.ceil(savedAssessments.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [savedAssessments, currentPage]);

  const deleteAssessment = async (id) => {
    try {
      const response = await authenticatedFetch(`/api/rail-del-history/${id}`, {
        method: 'DELETE',
      }, user.token, logout);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSavedAssessments(prev => prev.filter(a => a.scan_id !== id));
    } catch (e) {
      console.error("Failed to delete assessment:", e);
      setError("Failed to delete assessment. Please try again later.");
    }
  };

  const viewAssessment = (assessment) => {
     navigate(`/rail-analysis/${assessment.scan_id}`);
    //setSelectedAssessment(assessment);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAssessments = savedAssessments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(savedAssessments.length / itemsPerPage);

  // If viewing a specific assessment, show detail view
  if (selectedAssessment) {
    return (
      <div className="slide-in" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <button
          onClick={() => setSelectedAssessment(null)}
          style={{
            padding: '0.5rem 1rem',
            background: '#f1f5f9',
            border: '2px solid #cbd5e1',
            borderRadius: '0.5rem',
            color: '#475569',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            marginBottom: '1rem',
          }}
        >
          ← Back to List
        </button>
        
        <div style={{
          background: '#ffffff',
          borderRadius: '1rem',
          padding: '2rem',
          border: '2px solid #e2e8f0',
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
            {selectedAssessment.model.name}
          </h2>
          <div style={{ color: '#64748b', lineHeight: '1.6' }}>
            <p><strong>Assessment ID:</strong> {selectedAssessment.scan_id}</p>
            <p><strong>Timestamp:</strong> {new Date(selectedAssessment.timestamp).toLocaleString()}</p>
            <p><strong>Method:</strong> {selectedAssessment.jailbreakMethod.name}</p>
            <p><strong>Categories Tested:</strong> {selectedAssessment.categories.join(', ')}</p>
            
            <div style={{ marginTop: '1.5rem' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '0.5rem', color: '#1e293b' }}>
                Protected Results
              </h3>
              <p>Blocked: {selectedAssessment.protected.blocked}</p>
              <p>Allowed: {selectedAssessment.protected.allowed}</p>
              <p>Over-protected: {selectedAssessment.protected.overprotected}</p>
            </div>
            
            <div style={{ marginTop: '1.5rem' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '0.5rem', color: '#1e293b' }}>
                Unprotected Results
              </h3>
              <p>Blocked: {selectedAssessment.unprotected.blocked}</p>
              <p>Allowed: {selectedAssessment.unprotected.allowed}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="slide-in" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      <div style={{
        background: '#f8fafc',
        borderRadius: '1rem',
        padding: '2rem',
        border: '2px solid #e2e8f0',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        marginBottom: '2rem',
      }}>
        <h2 style={{ 
          fontSize: '1.5rem', 
          fontWeight: 600, 
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
        }}>
          <Shield size={24} style={{ color: '#3b82f6' }} />
          Comparison of results with and without our guardrail
        </h2>

        {error && (
          <div style={{ color: 'red', textAlign: 'center', padding: '1rem' }}>
            {error}
          </div>
        )}

        {savedAssessments.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#64748b',
          }}>
            <AlertTriangle size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>No saved assessments yet</p>
            <p style={{ fontSize: '0.9rem' }}>Run a new assessment and save it to see it here</p>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}>
            {currentAssessments.map((assessment) => {
              const date = new Date(assessment.created_at);
              const improvement = assessment.status === 'COMPLETED' ? calculateImprovement(assessment) : null;
              const isRunning = assessment.status === 'ON_GOING';

              return (
                <div
                  key={assessment.scan_id}
                  style={{
                    background: '#ffffff',
                    borderRadius: '0.75rem',
                    padding: '1.5rem',
                    border: `2px solid ${isRunning ? '#f59e0b' : '#e2e8f0'}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem',
                    opacity: isRunning ? 0.95 : 1,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                    }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        {assessment.target_llm}
                      </h3>
                      {isRunning ? (
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: '#f59e0b20',
                          color: '#f59e0b',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}>
                          <span style={{
                            width: '8px',
                            height: '8px',
                            background: '#f59e0b',
                            borderRadius: '50%',
                            animation: 'pulse 2s ease-in-out infinite',
                          }} />
                          Running...
                        </span>
                      ) : (
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: '#10b98120',
                          color: '#10b981',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}>
                          
                        </span>
                      )}
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      gap: '1.5rem',
                      fontSize: '0.875rem',
                      color: '#64748b',
                      marginBottom: '0.5rem',
                    }}>
                      <div>
                        <strong>Date:</strong> {date.toLocaleDateString()} {date.toLocaleTimeString()}
                      </div>
                      <div>
                        <strong>Categories:</strong> {assessment.query_type.length}
                      </div>
                      <div>
                        <strong>Method:</strong> {assessment.method}
                      </div>
                    </div>

                    {isRunning ? (
                      <p style={{
                        fontSize: '0.875rem',
                        color: '#64748b',
                        marginTop: '0.5rem',
                      }}>
                        Assessment in progress...
                      </p>
                    ) : (
                      <>
                      <div style={{
                        display: 'flex',
                        gap: '1.5rem',
                        fontSize: '0.875rem',
                      }}>
                        <div>
                          <span style={{ color: '#64748b' }}>Protected: </span>
                          <span style={{ color: '#ef4444', fontWeight: 600 }}>
                            {assessment.total_jailbroken} jailbreak
                          </span>
                          {' / '}
                          <span style={{ color: '#10b981', fontWeight: 600 }}>
                            {assessment.total_queries}
                          </span>
                          
                        </div>
                        <div>
                          <span style={{ color: '#64748b' }}>Unprotected: </span>
                          <span style={{ color: '#ef4444', fontWeight: 600 }}>
                            {assessment.original_total_jailbroken} jailbreak
                          </span>
                          {' / '}
                          <span style={{ color: '#10b981', fontWeight: 600 }}>
                            {assessment.total_queries} 
                          </span>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '1.5rem',
                        fontSize: '0.875rem',
                        marginTop: '0.5rem',
                      }}>
                        {/* <div>
                          <span style={{ color: '#64748b' }}>Over Protected: </span>
                          <span style={{ color: '#ef4444', fontWeight: 600 }}>
                            
                          </span>
                          {' / '}
                          <span style={{ color: '#10b981', fontWeight: 600 }}>
                            
                          </span>
                          
                        </div> */}
                        
                      </div> </>
                    )}
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                  }}>
                    {!isRunning && (
                      <button
                        onClick={() => viewAssessment(assessment)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                          border: 'none',
                          borderRadius: '0.5rem',
                          color: '#ffffff',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        View Details
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to ${isRunning ? 'cancel' : 'delete'} this assessment?`)) {
                          deleteAssessment(assessment.scan_id);
                        }
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#ffffff',
                        border: '2px solid #ef4444',
                        borderRadius: '0.5rem',
                        color: '#ef4444',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      <XCircle size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
            
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '1rem',
                gap: '1rem',
              }}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e2e8f0',
                    background: currentPage === 1 ? '#f1f5f9' : '#ffffff',
                    color: currentPage === 1 ? '#94a3b8' : '#475569',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ChevronLeft size={20} />
                </button>
                <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e2e8f0',
                    background: currentPage === totalPages ? '#f1f5f9' : '#ffffff',
                    color: currentPage === totalPages ? '#94a3b8' : '#475569',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryView;